import type { AdultAge, BuiltinAnswers } from './types';
import { isValidPhone, PHONE_ERROR } from './phone';
import { isValidDate } from './validate';

export const emptyBuiltin = (): BuiltinAnswers => ({
  firstName: '', lastName: '', ward: '', dateOfBirth: '', telephone: '', address: '', city: '', stateOrProvince: '',
  emergencyContact: '', emergencyPrimaryPhone: '', emergencySecondaryPhone: '',
  specialDiet: null, dietExplanation: '', allergies: null, allergyExplanation: '',
  medications: '', selfAdminister: null, chronicIllness: null, chronicIllnessExplanation: '',
  surgery: null, surgeryExplanation: '', otherLimitations: '', specialNeeds: '',
});

/** The Church's permission form, as its completed copies are named. */
export const STANDARD_FORM_NAME = 'Permission and Medical Release Form';
/**
 * A completed form's file name, in Drive and when downloaded: “Form name-Last-First-YYYY-MM-DD.pdf”, ending with the date
 * of birth, so two participants with the same name get different files. Characters a file name can't hold become spaces.
 */
export function completedFormFileName(b: Pick<BuiltinAnswers, 'firstName' | 'lastName' | 'dateOfBirth'>, formName: string): string {
  const part = (s: string) => s.replace(/[\\/:*?"<>|\u0000-\u001f]/g, ' ').replace(/\s+/g, ' ').trim();
  const name = [formName, b.lastName, b.firstName].map(part).filter(Boolean).join('-').slice(0, 200);
  return `${[name, part(b.dateOfBirth)].filter(Boolean).join('-')}.pdf`;
}

/** The participant's full name, “First Last”, as on the permission form. */
export const participantName = (b: Pick<BuiltinAnswers, 'firstName' | 'lastName'>): string => [b.firstName, b.lastName].map(part => part.trim()).filter(Boolean).join(' ');

/** “Street address, City, State”. */
export const fullAddress = (b: Pick<BuiltinAnswers, 'address' | 'city' | 'stateOrProvince'>): string => [b.address, b.city, b.stateOrProvince].map(part => part.trim()).filter(Boolean).join(', ');

/** Whole years between dob and `on` (both YYYY-MM-DD). */
export function ageOn(dob: string, on: string): number | null {
  if (!isValidDate(dob) || !isValidDate(on)) return null;
  const [dy, dm, dd] = dob.split('-').map(Number);
  const [oy, om, od] = on.split('-').map(Number);
  let age = oy - dy;
  if (om < dm || (om === dm && od < dd)) age--;
  return age;
}

/** The ages an organizer can choose from for signing alone. */
export const ADULT_AGES: readonly AdultAge[] = [18, 19, 21];
/** The event's age for signing alone. */
export const adultAgeOf = (event: { adultAge?: AdultAge }): AdultAge => event.adultAge ?? 18;
/** Whether the participant is `adultAge` or older on `on`, the day of signing, and so signs alone: false while the date
 * of birth isn't known. */
export function isAdult(dob: string, on: string, adultAge: AdultAge): boolean {
  const a = ageOn(dob, on);
  return a !== null && a >= adultAge;
}
/** Whether the participant is younger than `adultAge` on `on`, the day of signing, so a parent or guardian must also sign. */
export function isMinor(dob: string, on: string, adultAge: AdultAge): boolean {
  const a = ageOn(dob, on);
  return a !== null && a < adultAge;
}

export type BuiltinErrors = Partial<Record<keyof BuiltinAnswers, string>>;

/** `adult` when the participant signs alone, whose emergency contact needn't be a parent or guardian. */
export function validateContact(b: BuiltinAnswers, today: string, wardOptions: string[] = [], adult = false): BuiltinErrors {
  const e: BuiltinErrors = {};
  const req = (k: keyof BuiltinAnswers, message: string) => { if (!String(b[k] ?? '').trim()) e[k] = message; };
  req('firstName', 'Enter the participant’s first name.');
  req('lastName', 'Enter the participant’s last name.');
  if (wardOptions.length && !wardOptions.includes(b.ward)) e.ward = 'Choose the participant’s ward';
  if (!isValidDate(b.dateOfBirth)) e.dateOfBirth = b.dateOfBirth ? 'Enter a date like 3/15/2010.' : 'Choose the participant’s date of birth.';
  else if (b.dateOfBirth > today) e.dateOfBirth = 'Date of birth must be in the past';
  req('telephone', 'Enter a phone number.');
  for (const k of ['telephone', 'emergencyPrimaryPhone', 'emergencySecondaryPhone'] as const) {
    if (b[k]?.trim() && !isValidPhone(b[k])) e[k] = PHONE_ERROR;
  }
  req('address', 'Enter the street address.');
  req('city', 'Enter the city.');
  req('stateOrProvince', 'Enter the state or province.');
  req('emergencyContact', adult ? 'Enter the emergency contact’s name.' : 'Enter the parent or guardian’s name.');
  req('emergencyPrimaryPhone', 'Enter a phone number.');
  return e;
}

export function validateMedical(b: BuiltinAnswers): BuiltinErrors {
  const e: BuiltinErrors = {};
  for (const k of ['specialDiet', 'allergies'] as const) if (typeof b[k] !== 'boolean') e[k] = 'Choose Yes or No';
  if (b.specialDiet && !b.dietExplanation.trim()) e.dietExplanation = 'Please explain the dietary restrictions';
  if (b.allergies && !b.allergyExplanation.trim()) e.allergyExplanation = 'Please list the allergies';
  if (b.medications.trim() && typeof b.selfAdminister !== 'boolean') e.selfAdminister = 'Please answer yes or no';
  return e;
}

export function validateLimits(b: BuiltinAnswers): BuiltinErrors {
  const e: BuiltinErrors = {};
  for (const k of ['chronicIllness', 'surgery'] as const) if (typeof b[k] !== 'boolean') e[k] = 'Choose Yes or No';
  if (b.chronicIllness && !b.chronicIllnessExplanation.trim()) e.chronicIllnessExplanation = 'Please explain';
  if (b.surgery && !b.surgeryExplanation.trim()) e.surgeryExplanation = 'Please explain';
  return e;
}

/** The ward that goes on the form and in the sheet for this registration. */
export function participantWard(ev: { level: 'stake' | 'ward'; wardName: string | null }, b: Pick<BuiltinAnswers, 'ward'>): string {
  return ev.level === 'ward' ? ev.wardName ?? '' : b.ward.trim();
}

export function formatUsDate(iso: string): string {
  if (!isValidDate(iso)) return iso;
  const [y, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}/${y}`;
}
